import { BlurFilter, ITextStyle, TextMetrics, TextStyle } from 'pixi.js';
import { TextLine } from '../../../shape/shapes/text/text-line';
import { TextWord } from '../../../shape/shapes/text/text-word';
import { TextLetter } from '../../../shape/shapes/text/text-letter';
import gsap from 'gsap';
import {
  TextAnimationStep,
  ZoomAnimation,
} from '../../../animation/text-animation.service';
import { Subscription } from 'rxjs';
import {
  selectSelectedShape,
  selectShape,
  StudioAnimation,
  StudioCell,
  StudioTextAnimationStep,
} from '..';
import { StudioSceneShape } from '../../../shape/shapes/general/root/studio-scene-shape';
import { RectangleController } from '../../../../elements/util/rectangle-controller/rectangle-controller';
import { StudioShape } from '../shapes/studio-shape';
import { TextBreak } from '../layout/studio-layout.service';
import { GradientDirection } from '../../../shape/shapes/primitive/rectangle-element';
import { CanvasService } from '../../../../services/canvas/canvas.service';

export type StudioTextAnimationType =
  | 'zoom-fade'
  | 'fade-by-letters'
  | 'translate-fade';

export interface StudioShapeDescriptor {
  cell: StudioCell;
  metaPosition?: {
    vertical: 'top' | 'center' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
    shiftX?: number;
    shiftY?: number;
  };

  background?: StudioShapeBackground;
}

export interface StudioShapeBackground {
  type: 'color' | 'gradient';
  padding?: number;

  radius?: number;
  color?: string;
  opacity?: number;
  gradient?: {
    type: 'linear' | 'radial';
    direction?: GradientDirection;
    colors?: string[];
  };

  stroke?: {
    color: string;
    width: number;
  };
}

export interface StudioTextShapeDescriptor extends StudioShapeDescriptor {
  x?: number;
  y?: number;
  text?: string;

  textBreak?: TextBreak;
  lines?: Array<string[]>;
  dims?: [number, number];
  capitalization?: 'normal' | 'capital';
  align?: 'left' | 'center' | 'right';
  vAlign?: 'top' | 'center' | 'bottom';
  rowGap?: number;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold' | 'bolder' | 'lighter';
  fontStyle?: 'normal' | 'italic' | 'oblique';
  fill?: string;
  outline?: string;
  outlineWidth?: number;
}

export class StudioTextShape extends StudioShape<StudioTextShapeDescriptor> {
  lines: TextLine[] = [];
  selectionStartIndex: number = null;
  selectionEndIndex: number = null;
  currentSelectionIndex: number = null;

  selectedEditMode: boolean;
  selected?: boolean;
  lineHeight: number = 1;

  // Core properties used by TextLine/TextWord/TextLetter
  baseLineHeight: number = 0;
  maxLineWidth: number = 0;
  fontSize: number = 28;
  spaceWidth: number = 0;
  netLineHeight: number = 0;
  elementLevel: 'line' | 'word' | 'letter' = 'letter';
  characterWidthMap: Record<string, number> = {};
  loadPromise: Promise<void> = Promise.resolve();
  private letterIndex = 0;
  private cellSubscription: Subscription | null = null;
  get store() {
    return this.shape?.store;
  }

  get paddingValue() {
    return this.descriptor.cell.padding == undefined
      ? 0.01
      : (this.descriptor.cell.padding as number);
  }

  get words(): TextWord[] {
    return this.lines.reduce((arr, line) => [...arr, ...line.words], []);
  }

  get groupShapeParent() {
    return null;
  }

  get letters(): TextLetter[] {
    return this.words.reduce((arr, word) => [...arr, ...word.letters], []);
  }

  get grossLineHeight(): number {
    return this.netLineHeight * 1.25;
  }

  get lineHeightOffset(): number {
    return (this.grossLineHeight - this.netLineHeight) / 2;
  }

  get cs() {
    return this.shape?.cs;
  }

  get service() {
    return this.shape?.studioService?.shapeService;
  }

  get textConfig() {
    return { opacity: 1 };
  }

  get fontFamily() {
    return this.descriptor.fontFamily || this.studioService.currentFontFamily;
  }

  get elementStyle(): Partial<ITextStyle> {
    return {
      fontSize: this.fontSize,
      fontFamily: this.fontFamily,
      // fontWeight: this.descriptor.fontWeight || '600',
      fontWeight: '700',
      fontStyle: this.descriptor.fontStyle || 'normal',
      fill: this.descriptor.fill || '#000000ff',
      wordWrap: false,
      align: this.descriptor.cell.align || 'center',
    };
  }

  getType() {
    return 'studio-text-shape';
  }

  get x() {
    return this.descriptor.x;
  }
  get y() {
    return this.descriptor.y;
  }
  constructor(
    public readonly shape: StudioSceneShape,
    readonly id: string,
    descriptor: StudioTextShapeDescriptor,
  ) {
    super(shape, id, descriptor);
    // -- // -- //

    this.subscribeToCellDescriptor();
    this.init().then(() => {
      this.alignInCell(this.descriptor.cell);
      this.initRC();
    });
    // -- // -- //

    this.store.select(selectSelectedShape).subscribe(res => {
      const { id } = res || {};
      if (id == this.id) {
        this.selected = true;
        // this.rc.show();
      } else {
        this.selected = false;
        this.rc?.hide();
        // console.log('hide');
      }
    });
  }
  initContainers() {}
  update(descriptor: StudioTextShapeDescriptor) {
    this.descriptor = descriptor;
    this.init().then(() => {
      this.alignInCell(this.descriptor.cell);
      this.rc.patch({
        width: this.maxLineWidth,
        height: this.fontSize * 1.2 * this.lines.length,
      });
    });
  }
  alignInCell(cell: StudioCell) {
    // -- // -- // -- //

    this.descriptor.cell = cell;

    const { x, y, w, h } = this.cellDims;
    let { align, vAlign } = this.descriptor.cell;

    align ||= 'center';
    vAlign ||= 'center';

    const actualWidth = this.maxLineWidth;
    const actualHeight = this.baseLineHeight * this.lines.length;

    this.container.scale.set(1, 1);

    let containerX: number, containerY: number;

    switch (align) {
      case 'left':
        containerX = 0;
        break;
      case 'right':
        containerX = w - actualWidth;
        break;
      case 'center':
      default:
        containerX = (w - actualWidth) / 2;
        break;
    }

    switch (vAlign) {
      case 'top':
        containerY = 0;
        break;
      case 'bottom':
        containerY = h - actualHeight;
        break;
      case 'center':
      default:
        containerY = (h - actualHeight) / 2;
        break;
    }

    this.setPosition(x + containerX, y + containerY);
  }

  unMovedRC: [number, number];
  unmovedHeight: number;
  get isCenterBaseScale() {
    return false;
  }
  unMovedWidth: number;
  unmovedDims: [number, number];
  draggedDims: [number, number];
  rc: RectangleController;
  width: number;
  height: number;
  pseudoAnimation(animation: StudioAnimation): gsap.core.Timeline {
    const timeline = super.pseudoAnimation(animation);
    // const { letter, word } = animation as StudioTextAnimationStep;
    // if (letter) {
    //   this.elementLevel = 'letter';
    // }
    // if (word) {
    //   this.elementLevel = 'word';
    // }
    // this.refresh();
    return timeline;
  }
  initRC() {
    if (!this || !this.shape.text) {
      return;
    }

    const { x, y, w, h } = this.cellDims;

    this.rc = new RectangleController(
      this,
      {
        id: 'text:0',
        offset: [x, y],
        width: w,
        height: h,
        //height: this.baseLineHeight * this.lines.length,
        noStroke: false,
        fill: true,
        noScale: false,
        noRectRefresh: true,
        hiddenMode: true,
        fillOpacity: 0.001,
        centerBased: this.isCenterBaseScale,
        'stroke-width': 1,
        startDrag: () => {
          this.unmovedDims = [this.width, this.height];
          this.unmovedCoords = [this.container.x, this.container.y];
          this.unMovedRC = [this.rc.container.x, this.rc.container.y];
          this.unmovedHeight = this.fontSize * 1.2;
          this.unMovedWidth = this.netDims[0];
        },
        drag: resp => {
          // console.log('rc-drag', resp);
          const { width: _width, height: _height, x, y } = resp;
          let { dx, dy, dw, dh } = resp;

          if (this.isCenterBaseScale) {
            switch (resp.point) {
              case 'left-top':
                dw *= 2;
                break;
              case 'right-top':
                dx = -dw / 2;
                break;
              case 'left-bottom':
                dy = -dh / 2;
                dw *= 2;
                break;
              case 'right-bottom':
                dx = -dw / 2;
                dy = -dh / 2;
                break;
            }
          }
          // if (dy !== 0) {
          const width = this.unMovedWidth + dw;
          const height = this.unmovedHeight + dh;
          // this.descriptor.dims[0] = width;
          this.recalcFontSize(width, height);
          const newHeight = this.fontSize * 1.2;
          if (dy !== 0) {
            const dh = newHeight - this.unmovedHeight;
            dy = -dh;
            if (this.isCenterBaseScale) {
              dy /= 2;
            }
          }
          //   const dh = newHeight - this.unmovedHeight;
          //   // console.log('dh', dh); // -- //
          //   dy = -dh;
          //   if (this.isCenterBaseScale) {
          //     switch (resp.point) {
          //       case 'left-top':
          //       case 'right-top':
          //         // -- // -- // -- //
          //         break;
          //       case 'left-bottom':
          //         break;
          //       case 'right-bottom':
          //         break;
          //     }
          //   }
          // }

          const [newX, newY] = [
            this.unmovedCoords[0] + (dx || 0),
            this.unmovedCoords[1] + (dy || 0),
          ];

          this.container.x = this.unmovedCoords[0] + (dx || 0);
          this.container.y = this.unmovedCoords[1] + (dy || 0);
          this.rc.container.x = this.unMovedRC[0] + (dx || 0);
          this.rc.container.y = this.unMovedRC[1] + (dy || 0);
          this.draggedDims = [width, height];
          this.rc.updateRectangle({
            height: newHeight * this.lines.length,
            width,
          });
        },
        endDrag: () => {
          //
          if (!this.draggedDims) {
            return;
          }

          const [w, h] = this.grossDims;
          // const [x, y] = this.grossCoords;

          const [currentWidth, currentHeight] = this.draggedDims;
          // -- // -- // -- //
          this.descriptor.cell = {
            ...this.descriptor.cell,
            widthRatio: currentWidth / w,
            x: this.container.x / w,
            y: this.container.y / h,
          };

          this.rc.patch({
            height: this.fontSize * 1.2 * this.lines.length,
          });
          // console.log('endDrag', this.descriptor.cell);
          // console.log('endDrag', this.descriptor);
          this.save();
          // -- //
          // -- //
          // console.log('unMovedDims', this.unmovedDims);
          // console.log('draggedDims', this.draggedDims);
          // -- //
          // -- // -- //
          // const [_width, _height] = this.unmovedDims;
          // const [width, height] = this.draggedDims;

          // this.cell.w = width / _width;
        },
        clicked: () => this.onCellClicked(),
        mouseover: () => this.onMouseOver(),
        mouseout: () => this.onMouseOut(),
      },
      this.shape.circleContainer,
      this.shape.circleContainer,
    );
  }

  reset() {
    this.update(this.descriptor);
  }

  localStartDrag() {
    this.startBaseDrag();
  }

  drag(dx: number, dy: number) {
    this.dragTookPlace = true;
    const [x, y] = this.unmovedCoords;
    this.setPosition(x + dx, y + dy);
  }

  endDrag() {
    if (!this.dragTookPlace) return;
    // Cell's x, y are relative to the shape's width and height
    const [w, h] = this.grossDims;
    this.descriptor.cell.x = this.container.x / w;
    this.descriptor.cell.y = this.container.y / h;
    this.descriptor.cell.align = undefined;
    // -- // -- //
    this.save();
  }

  onMouseOver() {
    // console.log('onMouseOver');
    // this.rc?.rect.patch({ 'stroke-width': 1 });
    this.rc.show();
  }
  onMouseOut() {
    // console.log('onMouseOut');
    this.rc.hide();
    // this.rc?.rect.patch({ 'stroke-width': 0 });
  }
  onCellClicked() {
    this.rc?.show();
    this.store.dispatch(selectShape({ id: this.id }));
  }
  private subscribeToCellDescriptor() {
    if (!this.shape) {
      return;
    }
  }

  refresh() {
    this.lines.forEach(line => line.remove());
    this.lines = [];

    // Rebuild the text shape
    const { text, lines } = this.descriptor;
    const rows = lines || text.split('\n').map(line => [line]);
    if (rows && rows.length > 0) {
      this.measureAllCharacters(rows);
      this.buildLines(rows);
    }
  }

  private async init() {
    const { text, lines: linesFromDescriptor } = this.descriptor;
    // -- //
    console.log('init', this.fontFamily);
    await this.fontService._loadFont(this.fontFamily);
    const lines = linesFromDescriptor || text?.split('\n').map(line => [line]);
    const [width, height] = this.grossDims;

    const { widthRatio } = this.descriptor.cell;
    this.calculateFontSize(lines, width * (widthRatio || 1), height);
    this.refresh();
  }

  recalcFontSize(width: number, height: number) {
    const { dims } = this.descriptor;
    // const [w, h] = dims; //
    const { lines, text } = this.descriptor;
    const rows = lines || this.descriptor.text.split('\n').map(line => [line]);
    this.calculateFontSize(rows, width, height);
    this.measureAllCharacters(rows);
    this.buildLines(rows);
  }

  getDimensions() {
    return [this.maxLineWidth, this.baseLineHeight * this.lines.length];
  }

  get multipleLettersSelected() {
    return (
      this.selectionStartIndex != null &&
      this.selectionStartIndex !== this.selectionEndIndex
    );
  }

  updateSelection() {
    // this.lines.forEach(line => line.updateSelection());
  }

  get fontService() {
    return this.shape.studioService.shapeService.fontService;
  }

  static getFontSizeByCell(
    cs: CanvasService,
    cell: StudioCell,
    text: string,
    fontFamily: string,
  ): number {
    const lines = text.split('\n');
    const [screenX, screenY] = [cs.currentScreenX, cs.currentScreenY].map(
      val => val * cs.fitScale,
    );
    const { w, h } = cell;
    const targetWidth = screenX * w;
    const targetHeight = screenY * h;

    const style = new TextStyle({
      fontFamily,
      fontSize: 13,
    });

    const [, longestRow] = lines.reduce(
      ([longestWidth, longestText], row) => {
        // const rowText = row.join(' ');
        const metric = TextMetrics.measureText(row, style);
        if (longestWidth < metric.maxLineWidth) {
          return [metric.maxLineWidth, row];
        } else {
          return [longestWidth, longestText];
        }
      },
      [0, ''] as [number, string],
    );

    // Binary search for optimal font size
    let minSize = 1;
    let maxSize = Math.min(targetWidth, targetHeight);
    let optimalSize = minSize;

    console.log('targetWidth', targetWidth);
    while (minSize <= maxSize) {
      const midSize = Math.floor((minSize + maxSize) / 2);
      const testStyle = new TextStyle({
        ...style,
        fontSize: midSize,
      });

      const metric = TextMetrics.measureText(longestRow, testStyle);
      const totalHeight = metric.lineHeight * lines.length;

      if (metric.maxLineWidth <= targetWidth && totalHeight <= targetHeight) {
        optimalSize = midSize;
        minSize = midSize + 1;
      } else {
        maxSize = midSize - 1;
      }
    }

    return Math.max(1, optimalSize);
  }

  private calculateFontSize(
    rows: Array<string[]>,
    targetWidth: number,
    targetHeight: number,
  ): Promise<void> {
    // return new Promise(resolve => {
    // Find the longest row text
    if (this.descriptor.cell.fontSize) {
      this.fontSize = this.descriptor.cell.fontSize;
    } else {
      const _style = new TextStyle({
        ...this.elementStyle,
        fontSize: 20,
      });

      const [, longestRow] = rows.reduce(
        ([longestWidth, longestText], row) => {
          const rowText = row.join(' ');
          const metric = TextMetrics.measureText(rowText, _style);
          if (longestWidth < metric.maxLineWidth) {
            return [metric.maxLineWidth, rowText];
          } else {
            return [longestWidth, longestText];
          }
        },
        [0, ''] as [number, string],
      );

      if (!longestRow) {
        this.fontSize = 28;
        return;
      }

      // Binary search for optimal font size
      let minSize = 1;
      let maxSize = Math.min(targetWidth, targetHeight);
      let optimalSize = minSize;

      while (minSize <= maxSize) {
        const midSize = Math.floor((minSize + maxSize) / 2);
        const testStyle = new TextStyle({
          ...this.elementStyle,
          fontSize: midSize,
        });

        const metric = TextMetrics.measureText(longestRow, testStyle);
        const totalHeight = metric.lineHeight * rows.length;

        if (metric.maxLineWidth <= targetWidth && totalHeight <= targetHeight) {
          optimalSize = midSize;
          minSize = midSize + 1;
        } else {
          maxSize = midSize - 1;
        }
      }

      // optimalSize = 124;
      this.fontSize = Math.max(1, optimalSize);
    }

    const style = new TextStyle({
      ...this.elementStyle,
      fontSize: this.fontSize,
    });
    const metric = TextMetrics.measureText('Abc', style);
    this.baseLineHeight = metric.lineHeight;
    this.netLineHeight = metric.lineHeight;

    let maxWidth = 0;
    for (const row of rows) {
      const rowText = row.join(' ');
      const rowMetric = TextMetrics.measureText(rowText, style);
      maxWidth = Math.max(maxWidth, rowMetric.maxLineWidth);
    }
    this.maxLineWidth = maxWidth;

    // Calculate space width
    const outerMetric = TextMetrics.measureText('a', style);
    const spaceMetric = TextMetrics.measureText('a a', style);
    this.spaceWidth = spaceMetric.maxLineWidth - 2 * outerMetric.maxLineWidth;
  }

  private measureAllCharacters(rows: Array<string[]>) {
    this.characterWidthMap = {};
    const style = new TextStyle({
      ...this.elementStyle,
      fontSize: this.fontSize,
    });

    for (const row of rows) {
      for (const word of row) {
        for (const char of word) {
          if (this.characterWidthMap[char]) continue;
          if (char === ' ') {
            this.characterWidthMap[char] = this.spaceWidth;
            continue;
          }
          const metric = TextMetrics.measureText(char, style);
          this.characterWidthMap[char] = metric.maxLineWidth;
        }
      }
    }
    // Always add space
    this.characterWidthMap[' '] = this.spaceWidth;
  }

  private async buildLines(rows: Array<string[]>) {
    this.lines.forEach(line => line.remove());
    this.lines = [];
    this.letterIndex = 0;

    const style = new TextStyle({
      ...this.elementStyle,
      fontSize: this.fontSize,
    });
    const metric = TextMetrics.measureText('Abc', style);
    this.baseLineHeight = metric.lineHeight;

    rows.forEach((row, index) => {
      const wordsData = this.buildWordData(row);
      const lineWidth = wordsData.reduce((sum, w) => sum + w.width, 0);
      const line = new TextLine(
        this,
        {
          words: wordsData,
          wrapped: false,
          width: lineWidth,
        },
        {
          index,
          maxLineWidth: this.maxLineWidth,
          lineHeight: this.fontSize * 1.2,
          letterIndexOffset: 0,
          style: this.elementStyle as ITextStyle,
          width: lineWidth,
        },
      );

      this.lines.push(line);
    });
  }

  private buildWordData(
    words: string[],
  ): Array<{ text: string; width: number }> {
    const result: Array<{ text: string; width: number }> = [];

    words.forEach((word, i) => {
      const width = Array.from(word).reduce(
        (sum, char) => sum + (this.characterWidthMap[char] || 0),
        0,
      );
      result.push({ text: word, width });

      // Add space between words (except after last word)
      // if (i < row.length - 1) {
      //   result.push({ text: ' ', width: this.spaceWidth });
      // }
    });

    return result;
  }

  getGsapPosition(delay: number, overlap: number) {
    return delay !== undefined
      ? delay
      : overlap !== undefined
        ? `-=${overlap}`
        : 0;
  }

  getLetterIndex(): number {
    return this.letterIndex++;
  }

  // Stub methods that may be called by TextLine/TextWord/TextLetter
  removeSelection() {}
  showCursor() {}
  clicked() {}
  select(_params?: unknown) {}
  deselect(_params?: unknown) {}
  keyDownHandler(_key: string) {}
  _showCursor(_position: { x: number; y: number }, _word: TextWord) {}

  //
  destroy() {
    // Unsubscribe from cell descriptor changes
    if (this.cellSubscription) {
      this.cellSubscription.unsubscribe();
      this.cellSubscription = null;
    }

    this.lines.forEach(line => line.remove());
    this.container.destroy();
  }

  remove() {
    this.destroy();
  }

  baseHeight: number;
  /**
   * Animate the text shape with a given animation step
   */

  onStartFcns: Array<() => void> = [];
  gsapAnimateStep(animation: StudioTextAnimationStep) {
    this.onStartFcns = [];
    // console.log('text-shape > gsapAnimateStep', animation);

    const timeline = gsap.timeline();
    const {
      type: baseType,
      duration: initialDuration,
      zoom,
      translate,
      line,
      blur,
      forceFade,
      next,
      paralell,
      lineIndex,
      nextLine,
    } = animation;

    const duration = initialDuration || 1;

    this.baseHeight = this.fontSize * this.lines.length;

    if (baseType === 'appear') {
      if (!line) {
        // The line is defined the appear logic takes place in the line animation
        this.container.alpha = 0;
        timeline.add(gsap.to(this.container, { alpha: 1, duration }));
      }
    }

    // -- //
    if (zoom) {
      const zoomTimeline = this.gsapZoomAnimateStep(zoom, duration);
      timeline.add(zoomTimeline, 0);
    }

    if (translate) {
      const translateTimeline = this.gsapTranslateAnimateStep(
        animation,
        duration,
      );
      timeline.add(translateTimeline, 0);
    }

    // Handle line animation - check if lineIndex is defined for single line animation
    if (line) {
      const { endBefore } = line;
      const completeDuration = duration - (endBefore || 0);
      const itemDuration = this.studioService.getItemDuration(
        this.lines.length,
        completeDuration,
        line.overlap,
      );
      const lineTimelines = this.lines.map(line =>
        line.gsapAnimate({
          ...animation,
          duration: itemDuration,
        }),
      );
      // -- //
      this.onStartFcns.push(...lineTimelines.map(l => l.onStartFcns).flat());
      this.studioService.addAnimationChain(
        timeline,
        lineTimelines.map(l => l.timeline),
        {
          completeDuration,
          overlap: line.overlap,
        },
      );
    }

    // Handle letter-by-letter animation

    // Handle nextLine - chain the next line's animation to the timeline
    // if (nextLine) {
    //   const nextLineTimeline = this.gsapAnimateStep(nextLine);
    //   const overlapTime = nextLine.overlap ? duration * nextLine.overlap : 0;
    //   if (overlapTime > 0) {
    //     timeline.add(nextLineTimeline, `-=${overlapTime}`);
    //   } else {
    //     timeline.add(nextLineTimeline, `+=${nextLine.delay || 0}`);
    //   }
    // }

    if (blur) {
      const blurTimeline = gsap.timeline();
      const { from, duration: blurDuration } = blur;
      const blurFilter = new BlurFilter(from, 5);
      blurFilter.padding = Math.ceil(blur.to) * 2;
      this.container.filters = [blurFilter];
      timeline.add(blurTimeline, 0);
    }

    timeline.eventCallback('onStart', () => {
      this.onStartFcns.map(fcn => fcn());
    });

    return timeline;
  }

  /**
   * Animate a single line by its index
   */

  gsapTranslateAnimateStep(
    animation: StudioTextAnimationStep,
    duration: number,
  ) {
    const timeline = gsap.timeline();
    const { translate, type: baseType } = animation;
    const { from, to, duration: translateDuration } = translate;
    const dur = translateDuration || duration;

    if (baseType === 'appear' && from) {
      const originalX = this.container.x;
      const originalY = this.container.y;
      // Coords is [x, y] tuple
      this.container.x = originalX + (from[0] || 0);
      this.container.y = originalY + (from[1] || 0);
      this.container.alpha = 0;

      timeline.add(
        gsap.to(this.container, {
          duration: dur,
          x: originalX,
          y: originalY,
          alpha: 1,
          ease: 'power2.out',
        }),
        0,
      );
    } else if (baseType === 'disappear' && to) {
      timeline.add(
        gsap.to(this.container, {
          duration: dur,
          x: this.container.x + (to[0] || 0),
          y: this.container.y + (to[1] || 0),
          alpha: 0,
          ease: 'power2.in',
        }),
        0,
      );
    }
    return timeline;
  }

  gsapZoomAnimateStep(animation: ZoomAnimation, duration: number) {
    // -- // -- // -- //

    const timeline = gsap.timeline();
    const { from, to, duration: zoomDuration, translate } = animation;
    const [zoomDx, zoomDy] = translate || [0, 0];
    if (from) {
      // console.log('bounds', this.container.getBounds()); //
      // console.log('bounds', this.container.width, this.container.height); //
      timeline.eventCallback('onStart', () => {
        const fromScale = from;
        const previousWidth = this.maxLineWidth;
        const nextWidth = this.maxLineWidth * fromScale;

        const __dx = (previousWidth - nextWidth) / 2;
        const __dy = (this.baseHeight - this.baseHeight * fromScale) / 2;

        this.container.scale.x = fromScale;
        this.container.scale.y = fromScale;

        this.container.position.x += __dx;
        this.container.position.y += __dy;
      });

      this.container.scale.set(from);

      timeline.add(
        gsap.to(this.container.scale, {
          duration,
          x: to,
          y: to,
          alpha: 1,
          // ease: 'power2.out',
        }),
        0,
      );
    }

    if (to) {
      timeline.add(
        gsap.to(this.container.position, {
          duration: zoomDuration || duration || 1,
          x: (i, target) => {
            // Original
            const targetWidth = this.maxLineWidth * to;
            const currentWidth = this.maxLineWidth * this.container.scale.x;
            const dx = (targetWidth - currentWidth) / 2;
            return target.x - dx + zoomDx;
          },
          y: (i, target) => {
            const targetHeight = this.baseHeight * to;
            const currentHeight = this.baseHeight * this.container.scale.y;
            return target.y - (targetHeight - currentHeight) / 2 + zoomDy;
          },
          // ease: 'linear',
          // ease: 'power1.out',
        }),
        0,
      );
      timeline.add(
        gsap.to(this.container.scale, {
          duration,
          x: to,
          y: to,
          alpha: 0,
          // ease: 'power2.in',
        }),
        0,
      );
    }

    return timeline;
  }

  gsapLineAnimateStep(animation: StudioTextAnimationStep) {
    const timeline = gsap.timeline();

    const { line } = animation;
    const { delay } = line || {};

    const lineAnimations = this.lines.map(line => line.gsapAnimate(animation));

    // -- // -- //∂
    const lineStartFcns: Array<() => void> = [];
    lineAnimations.forEach((animation, index) => {
      const { timeline: lineTimeline, onStartFcns: lineOnStartFcns } =
        animation;
      timeline.add(lineTimeline, index * (delay || 0));
      lineStartFcns.push(...lineOnStartFcns);
    });
    // -- //
    return { timeline, onStartFcns: lineStartFcns };
  }

  /**
   * Generate animation step based on animation type
   */
  static generateAnimationStep(
    type: StudioTextAnimationType,
    options?: { duration?: number; translateFrom?: [number, number] },
  ): TextAnimationStep {
    const duration = options?.duration || 1.5;

    switch (type) {
      case 'zoom-fade':
        return {
          type: 'appear',
          duration,
          forceFade: true,
          zoom: {
            from: 0.8,
            to: 1,
          },
        };

      case 'fade-by-letters':
        return {
          type: 'appear',
          duration,
          letter: {},
        };

      case 'translate-fade':
        return {
          type: 'appear',
          duration,
          forceFade: true,
          translate: {
            type: 'absolute',
            from: options?.translateFrom || [0, 50],
          },
        };

      default:
        return {
          type: 'appear',
          duration,
          forceFade: true,
        };
    }
  }

  createContainerDropShadow() {
    // this.container.filters = [
    //   new DropShadowFilter({
    //     alpha: 0.35,
    //     blur: 24,
    //     color: 0x000000,
    //     distance: 0,
    //     quality: 2,
    //     rotation: 0,
    //   }),
    // ];
  }
  currentLetter: TextLetter;
  letterClicked(letter: TextLetter) {
    this.currentLetter = letter;
  }
}
